import { useEffect, useState, useSyncExternalStore } from 'react';
import { api } from '../../api/client.js';
import { outbox } from '../../offline/index.js';
import { queuePhotos, subscribePhotos } from '../../offline/photos.js';

// Incidents reported from this phone, kept until the server has them, so a
// report shows on the job at once with or without signal.
const KEY = 'nu.incidents';
const listeners = new Set();
let cache = null;

function readAll() {
  if (cache) return cache;
  try { cache = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { cache = []; }
  if (!Array.isArray(cache)) cache = [];
  return cache;
}
function writeAll(list) {
  // Keep a week.
  cache = list.filter((i) => Date.now() - Date.parse(i.happenedAt) < 7 * 864e5);
  try { localStorage.setItem(KEY, JSON.stringify(cache)); } catch { /* lasts this visit */ }
  listeners.forEach((fn) => fn());
}
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

// Send a report (queued if there's no signal) and its photos.
export async function reportIncident(job, { type, service, hurt, details, canContinue }, blobs = []) {
  const id = crypto.randomUUID();
  const at = new Date().toISOString();
  writeAll([...readAll(), { id, bookingId: job.id, type, service, hurt, details, canContinue, happenedAt: at, photos: blobs.length, local: true }]);
  await outbox.enqueue('job.incident', { id, bookingId: job.id, type, service: service || null, hurt, details, canContinue, at });
  await queuePhotos(job.id, blobs, 'incident', at, id);
  return id;
}

// A job's incidents: the server's (kept on the phone for no signal) plus any
// from this phone the server doesn't have yet.
export function useJobIncidents(bookingId) {
  const key = `nu.jobIncidents.${bookingId}`;
  const local = useSyncExternalStore(subscribe, readAll);
  const [server, setServer] = useState(() => {
    try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch { return []; }
  });
  useEffect(() => {
    let live = true;
    const load = () => api(`/my/jobs/${bookingId}/incidents`).then((list) => {
      if (!live) return;
      setServer(list);
      try { localStorage.setItem(key, JSON.stringify(list)); } catch { /* fine */ }
    }, () => {});
    load();
    const off = outbox.subscribe(load);
    const offPhotos = subscribePhotos(load);
    return () => { live = false; off(); offPhotos(); };
  }, [bookingId, key]);
  const known = new Set(server.map((i) => i.id));
  return [...server, ...local.filter((i) => i.bookingId === bookingId && !known.has(i.id))]
    .sort((a, b) => String(b.happenedAt).localeCompare(String(a.happenedAt)));
}
